import React from 'react';

import { AdvancedAnalyticsPermission } from './components/advanced-analytics-permission/advanced-analytics-permission';
import messages from './messages';
import { Permission } from './permission';
import styles from './styles.module.less';
import { emptyObject } from 'ui-components/default-props/empty';
import { useEnableAnalyticsRoleSelfService } from '../../../../../feature-flags/hooks/use-enable-analytics-role-self-service';
import { useInventoryManagementBasicFeatureGate } from '../../../../../feature-gates/use-inventory-management-basic-feature-gate';
import { useEnableOrderPickerRole } from '../../../../../hooks/use-get-order-picker-permissions';
import { UserPermissionService as Roles } from '../../../../../services/user-permission-service';
import { useIsSenderEmailsEnabledForSeller } from '../../../sender-emails/use-is-sender-emails-enabled-for-seller';

const permissionsMap = {
  [Roles.accountManagementPermissionRole]: {
    label: messages.account,
    details: messages.accountDetails,
  },
  [Roles.administrationPermissionRole]: {
    label: messages.administration,
    details: messages.administrationDetails,
  },
  [Roles.configurationPermissionRole]: {
    label: messages.configuration,
    details: messages.configurationDetails,
  },
  [Roles.customerManagementPermissionRole]: {
    label: messages.customerMgmt,
    details: messages.customerMgmtDetails,
  },
  [Roles.inventoryManagementRole]: {
    label: messages.inventoryMgmt,
    details: messages.inventoryMgmtDetails,
  },
  [Roles.mobilePickingManualVerificationRole]: {
    label: messages.mobilePickingManualVerification,
    details: messages.mobilePickingManualVerificationDetails,
  },
  [Roles.orderManagementPermissionRole]: {
    label: messages.orderMgmt,
    details: messages.orderMgmtDetails,
  },
  [Roles.orderPickingPermissionRole]: {
    label: messages.orderPickingRole,
    details: messages.orderPickingDetails,
  },
  [Roles.packingScanManualVerificationRole]: {
    label: messages.packingScanManualVerification,
    details: messages.packingScanManualVerificationDetails,
  },
  [Roles.productManagementPermissionRole]: {
    label: messages.productMgmt,
    details: messages.productMgmtDetails,
  },
  [Roles.purchasingPermissionRole]: {
    label: messages.purchasing,
    details: messages.purchasingDetails,
  },
  [Roles.reportingPermissionRole]: {
    label: messages.reporting,
    details: messages.reportingDetails,
  },
  [Roles.analyticsViewRole]: {
    label: messages.advancedAnalytics,
  },
  [Roles.shippingPermissionRole]: {
    label: messages.shipping,
    details: messages.shippingDetails,
  },
  [Roles.toteManagementCrudRole]: {
    label: messages.toteManagementCrud,
    details: messages.toteManagementCrudDetails,
  },
};

/**
 * Determines which tooltip message to show based on conditions
 * @param {boolean} needsContactAdminTooltip - Whether the user needs to contact admin
 * @returns {import('../../../../../prop-types/intl-types').MessageDescriptor} The appropriate tooltip message
 */
const getTooltipMessage = (needsContactAdminTooltip) => {
  if (needsContactAdminTooltip) {
    return messages.contactAdmin;
  }

  return messages.cannotEditSelf;
};

/**
 * Permissions component for displaying and managing user role permissions
 * @param {object} props - Component props
 * @param {object} [props.permissionStatuses] - Current permission statuses keyed by role ID
 * @param {(id: string, value: boolean) => void} props.updateForm - Callback when a permission is toggled
 * @param {boolean} [props.disabled] - Whether all permissions should be disabled
 * @returns {JSX.Element} Permissions component
 */
export const Permissions = ({ permissionStatuses = emptyObject, updateForm, disabled = false, currentUser = emptyObject }) => {
  const isAnalyticsRoleSelfServiceEnabled = useEnableAnalyticsRoleSelfService();
  const { isFeatureEnabled: hasInventoryFeature } = useInventoryManagementBasicFeatureGate();
  const enableSenderEmails = useIsSenderEmailsEnabledForSeller();
  const hasOrderPickingRole = useEnableOrderPickerRole();

  return (
    <div className={styles.permissions}>
      {Object.keys(permissionsMap).map((id) => {
        if (
          (id === Roles.inventoryManagementRole && !hasInventoryFeature) ||
          (id === Roles.orderPickingPermissionRole && !hasOrderPickingRole)
        ) {
          return null;
        }

        if (id === Roles.analyticsViewRole) {
          if (!isAnalyticsRoleSelfServiceEnabled) {
            return null;
          }
          return (
            <AdvancedAnalyticsPermission
              key={id}
              currentUser={currentUser}
              disabled={disabled}
              permissionStatuses={permissionStatuses}
              updateForm={updateForm}
            />
          );
        }

        const needsContactAdminTooltip = !disabled && id === Roles.orderPickingPermissionRole && !hasOrderPickingRole;
        const isCheckBoxDisabled = disabled || needsContactAdminTooltip;

        const permissionDetails =
          id === Roles.configurationPermissionRole && enableSenderEmails
            ? messages.configurationDetailsWithSenderEmails
            : permissionsMap[id].details;

        return (
          <Permission
            key={id}
            details={permissionDetails}
            disabled={isCheckBoxDisabled}
            id={id}
            isChecked={permissionStatuses[id]}
            label={permissionsMap[id].label}
            onChange={updateForm}
            tooltipMessage={getTooltipMessage(needsContactAdminTooltip)}
          />
        );
      })}
    </div>
  );
};
